import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  ReportingStructureResponse,
  UpdateUserProfileRequest,
  UserDetailsResponse,
  UserProfile,
  WorksWithResponse,
} from '../models/profile-data.models';
import { ApiHelperService } from './api-helper.service';
import { SKIP_RETRY_INTERCEPTOR } from '../interceptors/retry.interceptor';
import { environment } from '../../../environments/environment';

const GRAPH_ME_URL =
  'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,jobTitle,givenName,officeLocation,department,mobilePhone,businessPhones';

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

const GRAPH_WORKS_WITH =
  'https://graph.microsoft.com/v1.0/me/people?%24select=id%2CdisplayName%2CuserPrincipalName%2CjobTitle&%24filter=personType%2Fsubclass%20ne%20%27ImplicitContact%27&%24top=10';

const GRAPH_USERS_URL = 'https://graph.microsoft.com/v1.0/users';

const GET_TOPICS = 'user/v1/topics';

const UPDATE_USER_PROFILE = 'user/v1/update-profile';

const GET_USER_DETAILS = 'user/v1/me';

const GET_BANNER = 'user/v1/banner';
const POST_BANNER = 'user/v1/user-banner';

const UPLOAD_BANNER = 'user/v1/upload-banner';

@Injectable({ providedIn: 'root' })
export class ProfileDataService implements OnDestroy {
  private readonly http = inject(HttpClient);

  private readonly apiHelper = inject(ApiHelperService);

  private readonly _error = new BehaviorSubject<string | null>(null);

  readonly error$ = this._error.asObservable();

  /** Fetches user profile fields from Microsoft Graph. */
  getProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(GRAPH_ME_URL)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Fetches another user's profile fields from Microsoft Graph. */
  getUserProfile(userEmail: string): Observable<UserProfile> {
    const email = encodeURIComponent((userEmail || '').trim());
    const url =
      `${GRAPH_USERS_URL}/${email}` +
      '?$select=id,displayName,mail,userPrincipalName,jobTitle,givenName,officeLocation,department,mobilePhone,businessPhones';

    return this.http
      .get<UserProfile>(url)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Fetches people the user works with from Microsoft Graph. */
  getWorksWith(): Observable<WorksWithResponse> {
    return this.apiHelper.get<WorksWithResponse>(GRAPH_WORKS_WITH, {
      headers: {
        ConsistencyLevel: 'eventual',
      },
    });
  }

  /** Fetches people a redirected user works with from Microsoft Graph. */
  getWorksWithForUser(userEmail: string): Observable<WorksWithResponse> {
    const email = encodeURIComponent((userEmail || '').trim());
    const url =
      `${GRAPH_USERS_URL}/${email}/people` +
      '?$select=id,displayName,userPrincipalName,jobTitle' +
      "&$filter=personType/subclass ne 'ImplicitContact'" +
      '&$top=10';

    return this.apiHelper.get<WorksWithResponse>(url, {
      headers: {
        ConsistencyLevel: 'eventual',
      },
    });
  }

  /** Fetches people the user works with from Microsoft Graph. */
  getReportingStructure(userEmail: string): Observable<ReportingStructureResponse> {
    const email = encodeURIComponent((userEmail || '').trim());

    const url =
      `${GRAPH_USERS_URL}/${email}` +
      '?$select=id,displayName,mail,jobTitle' +
      '&$expand=manager($levels=max;$select=id,displayName,mail,jobTitle)';

    return this.apiHelper.get<ReportingStructureResponse>(url, {
      headers: {
        ConsistencyLevel: 'eventual',
      },
    });
  }

  /** Fetches topics which user has selected. */
  getTopics(): Observable<any> {
    return this.http
      .get(`${environment.apiBaseUrl}/${GET_TOPICS}`)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Fetches user details from the intranet API. */
  getUserDetails(): Observable<any> {
    const context = new HttpContext().set(SKIP_RETRY_INTERCEPTOR, true);
    return this.http
      .get(`${environment.apiBaseUrl}/${GET_USER_DETAILS}`, { context })
      .pipe(catchError((err: HttpErrorResponse) => throwError(() => err)));
  }

  /** Fetches the employee's profile banner URL. Returns null when no banner is set. */
  getBanner(): Observable<string | null> {
    const context = new HttpContext().set(SKIP_RETRY_INTERCEPTOR, true);

    return this.http
      .get(`${environment.apiBaseUrl}/${GET_BANNER}`, { responseType: 'blob', context })
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /** Fetches a redirected employee's profile banner URL by posting their email id. */
  getBannerForUser(emailId: string): Observable<string | null> {
    const normalizedEmailId = emailId.trim();
    if (!normalizedEmailId) {
      return of(null);
    }

    const context = new HttpContext().set(SKIP_RETRY_INTERCEPTOR, true);

    return this.http
      .post(
        `${environment.apiBaseUrl}/${POST_BANNER}`,
        { email: normalizedEmailId },
        { responseType: 'blob', context },
      )
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /** Uploads a new profile banner image for the logged-in employee. */
  uploadBanner(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('banner', file);
    return this.http
      .post<{ banner: string | null }>(`${environment.apiBaseUrl}/${UPLOAD_BANNER}`, formData)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Updates user profile details, including onboarding completion status. */
  updateUserProfile(payload: UpdateUserProfileRequest): Observable<UserDetailsResponse> {
    return this.http
      .put<UserDetailsResponse>(`${environment.apiBaseUrl}/${UPDATE_USER_PROFILE}`, payload)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Fetches the user's profile photo as a Blob and converts it to an
   * object URL. Returns null when no photo is set (404) so callers can
   * fall back to initials / placeholder without treating it as an error.
   */
  getProfilePhoto(): Observable<string | null> {
    return this.http.get(GRAPH_PHOTO_URL, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404 || err.status === 401) {
          return of(null);
        }
        // Graph returns non-404 with error.code "ImageNotFound" when no photo is set
        if (this.isImageNotFound(err)) {
          return of(null);
        }
        return this.handleError(err);
      }),
    );
  }

  /** Fetches another user's profile photo from Microsoft Graph. */
  getUserProfilePhoto(userIdOrUpn: string): Observable<string | null> {
    const identifier = (userIdOrUpn || '').trim();
    if (!identifier) {
      return of(null);
    }

    const url = `${GRAPH_USERS_URL}/${encodeURIComponent(identifier)}/photo/$value`;
    const context = new HttpContext().set(SKIP_RETRY_INTERCEPTOR, true);

    return this.http.get(url, { responseType: 'blob', context }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404 || err.status === 401) {
          return of(null);
        }
        if (this.isImageNotFound(err)) {
          return of(null);
        }
        return this.handleError(err);
      }),
    );
  }

  private isImageNotFound(err: HttpErrorResponse): boolean {
    try {
      const body = typeof err.error === 'string' ? JSON.parse(err.error) : err.error;
      return body?.error?.code === 'ImageNotFound';
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this._error.complete();
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    return throwError(() => new Error(this.errorMessage(err)));
  }

  private errorMessage(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'Network error — please check your connection.';
    }
    if (err.status === 401) {
      return 'Unauthorised — please sign in again.';
    }
    if (err.status === 403) {
      return 'Access denied — missing required Graph API permissions.';
    }
    if (err.status === 429) {
      return 'Too many requests — please try again shortly.';
    }
    if (err.status >= 500) {
      return 'Microsoft Graph service error — please try again later.';
    }
    return err.message ?? 'An unexpected error occurred.';
  }
}
